<template>
  <QCard class="request-callback-dialog full-width" style="max-width: 1024px" v-if="showDialog">
    <QCardSection class="column items-center q-pa-none q-ma-none full-width">
      <QCardSection class="row q-pa-none q-mb-50 full-width align-center">
        <QCardSection class="flex-1 q-pa-none">
          <p class="text-secondary text-weight-bold q-pa-none q-ma-none request-callback-title">Request a call</p>
        </QCardSection>
        <QBtn
          rounded
          class="calculator-dialog-close"
          flat
          :icon-right="closeIconCircle"
          size="16px"
          color="primary"
          align="left"
          @click="handleClose"
        >
          Close
        </QBtn>
      </QCardSection>
      <LoadingVehicle v-if="isLoading" />
      <QCardSection class="q-pa-none" style="width: 100%" v-if="!showThankYou && !isLoading">
        <h5 class="text-primary">Contact details</h5>
        <QForm ref="requestForm" class="request-callback-form" @submit.prevent="submitRequestCallback">
          <div class="row q-col-gutter-md">
            <InputField
              containerClass="col-6"
              type="name"
              label="Customer first name"
              placeholder="First name"
              v-model="form.firstName"
              :required="true"
              :hasRequiredLabel="false"
            />
            <InputField
              containerClass="col-6"
              type="name"
              label="Customer last name"
              placeholder="Last name"
              v-model="form.lastName"
              :required="true"
              :hasRequiredLabel="false"
            />
            <InputField
              containerClass="col-6"
              type="tel"
              label="Contact number"
              placeholder="Contact number"
              v-model="form.contactNumber"
              :required="true"
              :hasRequiredLabel="false"
            />
            <InputField
              containerClass="col-6"
              type="email"
              label="Email address"
              placeholder="Email address"
              v-model="form.email"
              :required="true"
              :hasRequiredLabel="false"
            />
            <InputField
              containerClass="col-12"
              label="Leave a message"
              type="textarea"
              :maxLength="250"
              :placeholder="handleInput(form.message, 'Type your message...')"
              v-model="form.message"
              :disable="isFormSubmitting"
              :hasRequiredLabel="false"
            />

            <div class="col-12 row q-mt-md">
              <p class="privacy-label text-weight-bold text-secondary">Privacy statement</p>
              <p class="privacy-description">
                By providing your details, you consent to us contacting you about our products and services (including comprehensive motor
                vehicle insurance). <a :href="privacyPolicyLink" target="_blank">FleetPartners Privacy Policy</a> contains information about
                how you can access and correct your personal information, how to make a complaint, and how we deal with complaints.
              </p>
            </div>
            <div class="col-12 req-btn-cont">
              <CalculatorButton
                color="primary"
                rounded
                hasChevronIcon
                label="Submit"
                outlined
                removeOpacity
                :type="'submit'"
                :disabled="isFormInvalid"
              />
            </div>
          </div>
        </QForm>
      </QCardSection>
      <MainHeadline
        align="center"
        header="Thank you for submitting the callback."
        body="We will respond as soon as possible."
        v-if="showThankYou && !isLoading"
      />
    </QCardSection>
  </QCard>
</template>

<script>
import { QCard, QCardSection, QBtn, QForm } from 'quasar';
import { evaCloseCircleOutline, evaCheckmark, evaClose } from '@quasar/extras/eva-icons';
import InputField from '../../Atoms/InputField';
import MutationRequestCallback from '../../../graphql/mutations/MutateSendRequestCallback';
import { mapState, mapGetters } from 'vuex';
import MainHeadline from '../../Atoms/MainHeadline';
import LoadingVehicle from '../../LoadingVehicle/LoadingVehicle';
import { getSubdomain } from '../../../utils/get-subdomain';
import CalculatorButton from '../CalculatorButton.vue';
import { GTM_EVENT_NAME_SME_REQUEST_CALL, GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR } from '../../../utils/constants';
export default {
  name: 'RequestCallbackModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    defaultData: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    ...mapGetters({
      isNZ: 'theme/isNZ'
    }),
    ...mapState({
      vehicleApplication: (state) => state.vehicleApplication.vehicleApplication
    }),
    getApplicationId() {
      return this.vehicleApplication?.applicationId;
    },
    privacyPolicyLink() {
      return this.isNZ ? 'https://www.fleetpartners.co.nz/privacy-policy' : 'https://www.fleetpartners.com.au/privacy-policy';
    }
  },
  data() {
    return {
      isLoading: false,
      isFormSubmitting: false,
      isFormInvalid: true,
      form: {
        firstName: this.defaultData?.firstName || '',
        lastName: this.defaultData?.lastName || '',
        contactNumber: this.defaultData?.contactNumber || '',
        email: this.defaultData?.email || '',
        message: this.defaultData?.message || ''
      },
      showDialog: this.value,
      showThankYou: false
    };
  },
  methods: {
    handleClose: async function () {
      this.showDialog = false;
      this.showThankYou = false;
    },
    handleFormValidation() {
      const requestForm = this.$refs.requestForm.$children.filter((child) => Object.keys(child.$refs).some((key) => key == 'field'));

      this.isFormInvalid = requestForm.some((field) => field.hasError);
    },
    submitRequestCallback: async function () {
      this.isLoading = true;
      this.form.domain = getSubdomain(this.$route) || 'sme-customers1-nz-uat.eclipx.xyz';
      const response = await this.$apollo.mutate({
        mutation: MutationRequestCallback,
        variables: {
          payload: { ...this.form }
        }
      });

      setTimeout(() => {
        this.$gtm.trackEvent({
          event: GTM_EVENT_NAME_SME_REQUEST_CALL,
          data: {
            ...GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR,
            ...(this.form?.firstName?.length && { firstName: this.form?.firstName }),
            ...(this.form?.lastName?.length && { lastName: this.form?.lastName }),
            ...(this.form?.email?.length && { email: this.form?.email }),
            ...(this.form?.contactNumber?.length && { phoneNumber: this.form?.contactNumber })
          }
        });
      });

      if (!response.data) {
        this.isLoading = false;
        return;
      }
      this.form = {
        firstName: '',
        lastName: '',
        contactNumber: '',
        email: '',
        message: ''
      };
      this.showThankYou = true;
      this.isLoading = false;
    },

    handleInput(value, placeholder) {
      return value ? value : placeholder;
    }
  },
  created() {
    this.closeIconCircle = evaCloseCircleOutline;
    this.checkIcon = evaCheckmark;
    this.closeIcon = evaClose;
  },
  watch: {
    'value'(newValue) {
      this.showDialog = newValue;
    },
    'showDialog'(value) {
      this.$emit('input', value);
    },
    'form.firstName'(value) {
      this.handleFormValidation();
    },
    'form.lastName'(value) {
      this.handleFormValidation();
    },
    'form.contactNumber'(value) {
      this.handleFormValidation();
    },
    'form.email'(value) {
      this.handleFormValidation();
    }
  },
  components: { QCard, QCardSection, QBtn, QForm, InputField, MainHeadline, LoadingVehicle, CalculatorButton }
};
</script>

<style lang="sass">
.req-btn-cont
  padding-top: 30px
  text-align: right
  .q-btn__content
    gap: 20px

.request-callback-dialog
  padding: 25px 30px 25px 25px
.request-callback-form label
  font-weight: bold;
.request-callback-dialog
  .request-callback-container
    justify-content: center
  .request-callback-icon
      width: 34px
      height: 24px
  .request-callback-label
    font-weight: 600
    text-wrap: pretty;

  .request-callback-title
    font-size: 24px
    align-self: left
    justify-self: left

  .request-callback-content
    display: flex
    flex-flow: row wrap
    gap: 8px
  .request-callback-footer
    padding: 0
    margin-top: 15px
    margin-bottom: 25px

  .request-callback-cardf
    width: calc(50% - 4px)

  .calculator-dialog-close.q-btn
    text-transform: capitalize;
    align-self: end
    .q-btn__wrapper
      padding: 0 20px
      .q-btn__content .q-icon
        margin-left: 8px
  .align-center
    align-items: center
  .flex-1
    flex: 1 1 auto
  .q-mb-50
    margin-bottom: 50px
  .privacy-label
    font-size: 18px
  @media (min-width:1024px)

    .request-callback-form .form-btn
      margin-top: 20px
      display: block
      margin-left: auto

  @media (max-width:768px)
    .request-callback-card
      width: 100%
    .calculator-dialog-close.q-btn .q-btn__wrapper
      padding: 0

  @media (max-width: 430px)
    .request-callback-label
      max-width: 250px

  @media (max-width: 375px)
    .request-callback-label
      max-width: 145px

    .request-callback-footer
      margin-bottom: 0
</style>
